import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Alert,
  FormControlLabel,
  LinearProgress,
  Switch,
  Tooltip,
} from "@mui/material";
import { MRT_ColumnDef, MaterialReactTable } from "material-react-table";
import { MRT_Localization_DE } from "material-react-table/locales/de";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import Modal from "@/components/modal";
import { RootState } from "@/components/state/store";
import type {
  CompaniesMissingRequiredFilters,
  CompaniesMissingRequiredStatus,
  CompanyMissingRequiredRow,
} from "@/components/anchorData/useCompaniesMissingRequiredAnchorData";
import styles from "@/components/anchorData/companiesMissingRequiredModal.module.css";

const NEW_WORK_CHECKED_SWITCH_SX = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--newWork)" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "var(--newWork)",
  },
};

const NEW_WORK_PROGRESS_SX = {
  backgroundColor: "var(--newWork30)",
  "& .MuiLinearProgress-bar": { backgroundColor: "var(--newWork)" },
};

interface CompaniesMissingRequiredModalProps {
  open: boolean;
  status: CompaniesMissingRequiredStatus;
  rows: CompanyMissingRequiredRow[];
  filters: CompaniesMissingRequiredFilters;
  onFiltersChange: (filters: CompaniesMissingRequiredFilters) => void;
  onSelectCompany: (company: CompanyMissingRequiredRow) => void;
  onClose: () => void;
}

const CompaniesMissingRequiredModal: FC<CompaniesMissingRequiredModalProps> = ({
  open,
  status,
  rows,
  filters,
  onFiltersChange,
  onSelectCompany,
  onClose,
}) => {
  const { t } = useTranslation();
  const preferredLanguage = useSelector(
    (state: RootState) => state.login.preferred_language
  );
  const isLoading = status === "loading";
  const isLoadingWithoutRows = isLoading && rows.length === 0;

  const columns = useMemo<MRT_ColumnDef<CompanyMissingRequiredRow>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: t("company"),
      },
      {
        id: "customer_id",
        accessorFn: (company) => company.customer_id ?? "",
        header: t("customer_id"),
      },
      {
        id: "parent_name",
        accessorFn: (company) => company.parent_name ?? "",
        header: t("parent_company"),
      },
      {
        id: "missing_required",
        accessorFn: (company) => company.missing_required_labels.join(", "), 
        header: t("anchordata_category_missing_required"),
        sortingFn: (rowA, rowB) =>
          rowA.original.missing_required_labels.length -
          rowB.original.missing_required_labels.length,
        Cell: ({ row }) => (
          <Tooltip
            title={
              <ul className={styles.missingLabelList}>
                {row.original.missing_required_labels.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            }
          >
            <span className={styles.missingCount}>
              {row.original.missing_required_labels.length}
            </span>
          </Tooltip>
        ),
      },
    ],
    [t]
  );


  return (
    <Modal
      open={open}
      title={t("anchordata_incomplete_companies_title")}
      hideSubmitButton
      cancelButtonText={t("close")}
      onClose={onClose}
      maxWidth="90vw"
      paperBorderRadius="0.75rem"
      contentClassName={styles.content}
    >
      <p className={styles.description}>
        {t("anchordata_incomplete_companies_description")}
      </p>

      <div className={styles.filters}>
        <FormControlLabel
          label={t("include_testcompanies")}
          control={
            <Switch
              sx={NEW_WORK_CHECKED_SWITCH_SX}
              checked={filters.includeTestCompanies}
              onChange={(event) =>
                onFiltersChange({
                  ...filters,
                  includeTestCompanies: event.target.checked,
                })
              }
            />
          }
        />
        <FormControlLabel
          label={t("parents_only")}
          control={
            <Switch
              sx={NEW_WORK_CHECKED_SWITCH_SX}
              checked={filters.parentsOnly}
              onChange={(event) =>
                onFiltersChange({
                  ...filters,
                  parentsOnly: event.target.checked,
                })
              }
            />
          }
        />
      </div>

      {isLoadingWithoutRows && (
        <>
          <p className={styles.loadingText}>
            {t("anchordata_incomplete_companies_loading")}
          </p>
          <LinearProgress sx={NEW_WORK_PROGRESS_SX} />
        </>
      )}

      {status === "error" && (
        <Alert severity="error">
          {t("anchordata_incomplete_companies_error")}
        </Alert>
      )}

      {rows.length > 0 ? (
        <MaterialReactTable
          localization={
            preferredLanguage === "en"
              ? MRT_Localization_EN
              : MRT_Localization_DE
          }
          columns={columns}
          data={rows}
          getRowId={(company) => company.subject_id}
          enableFullScreenToggle={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableHiding={false}
          enableDensityToggle={false}
          enableRowSelection={false}
          enableRowActions={false}
          state={{ showProgressBars: isLoading }}
          muiLinearProgressProps={{ sx: NEW_WORK_PROGRESS_SX }}
          globalFilterFn="contains"
          positionGlobalFilter="left"
          initialState={{
            density: "compact",
            showGlobalFilter: true,
            sorting: [{ id: "name", desc: false }],
            pagination: { pageIndex: 0, pageSize: 25 },
          }}
          enableStickyHeader
          muiTablePaperProps={{
            elevation: 0,
            className: styles.tablePaper,
            sx: { width: "100%" },
          }}
          muiTableBodyCellProps={{ sx: { whiteSpace: "normal" } }}
          muiTableBodyRowProps={({ row }) => ({
            tabIndex: 0,
            onClick: () => onSelectCompany(row.original),
            onKeyDown: (event) => {
              if (event.key === "Enter") {
                onSelectCompany(row.original);
              }
            },
            sx: {
              cursor: "pointer",
              backgroundColor: row.index % 2 === 0 ? "#f5f5f5" : "#fff",
            },
          })}
        />
      ) : (
        status === "done" && (
          <p className={styles.empty}>
            {t("anchordata_incomplete_companies_empty")}
          </p>
        )
      )}
    </Modal>
  );
};

export default CompaniesMissingRequiredModal;
